import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  Self,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Inject,
  Optional
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormControl,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
  NgControl,
  Validators,
} from '@angular/forms';
import { MatError, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
  tap,
} from 'rxjs/operators';
import { DadataService } from '@core/services/dadata.service';
import { IAddressModel } from '@shared/models/addresses/addresses.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-dadata-address',
  templateUrl: './dadata-address.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatError,
    MatTooltipModule,
    MatIconModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DadataAddressComponent implements ControlValueAccessor, OnInit {
  @Input() label: string | null = '';
  @Input() matTooltipLabel: string | null = null;
  @Input() type: 'address' | 'city' = 'address';
  @Input() placeholder: string | null = null;
  protected destroyRef: DestroyRef = inject(DestroyRef);
  isAddressSelected = false;
  suggestions: IAddressModel[] = [];
  private detectChanges = inject(ChangeDetectorRef);

  private onChange: (value: any) => void = () => {};
  public onTouched: () => void = () => {};

  constructor(
    @Optional() @Self() @Inject(NgControl) public controlDir: NgControl,
    private _dadataService: DadataService
  ) {
    if (this.controlDir) {
      this.controlDir.valueAccessor = this;
    }
  }

  get control(): FormControl {
    return this.controlDir?.control as FormControl;
  }

  ngOnInit(): void {
    if (!this.controlDir) {
      console.warn('DadataAddressComponent: NgControl not found');
      return;
    }

    const originalValidator = this.control.validator;

    this.control.setValidators((control: AbstractControl) => {
      const originalErrors = originalValidator ? originalValidator(control) : null;
      const customErrors = this.addressValidator()(control);
      
      return customErrors || originalErrors;
    });

    this.control.valueChanges
      .pipe(
        filter((query: any) => {
          if (typeof query === 'object' && query !== null) {
            this.isAddressSelected = true;
            return false;
          }
          if (typeof query === 'string') {
            this.isAddressSelected = false;
            return query.length > 3;
          }
          return false;
        }),
        debounceTime(1000),
        distinctUntilChanged(),
        tap(() => {
          this.control.updateValueAndValidity({ emitEvent: false });
        }),
        switchMap((value) => {
          if (this.type === 'city') {
            return this._dadataService.searchCity$((value || '') as string);
          }
          return this._dadataService.searchAddress$((value || '') as string);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(
        (query: {
          errors: boolean;
          data: IAddressModel[];
          success: boolean;
        }) => {
          this.suggestions = query.data || [];
          this.detectChanges.markForCheck();
        }
      );
  }

  displayFn = (address: IAddressModel): string => {
    if (!address) return '';
    return address.result || '';
  };

  writeValue(value: IAddressModel | null): void {
    if (value && typeof value === 'object') {
      this.isAddressSelected = true;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.control?.disable();
    } else {
      this.control?.enable();
    }
  }

  onOptionSelected(event: any): void {
    this.isAddressSelected = true;
    const selectedValue = event.option.value;
    this.onChange(selectedValue);
    this.control?.markAsTouched();
    this.onTouched();
  }

  private addressValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return this.control?.hasValidator(Validators.required) ? { required: true } : null;
      }
      if (typeof control.value === 'string' && control.value.trim().length > 0 && !this.isAddressSelected) {
        return { notSelected: true };
      }
      return null;
    };
  }

  public isRequired(): boolean {
    if (!this.control) return false;
    
    // Check if the control has the required validator
    if (this.control.validator) {
      const validator = this.control.validator({} as AbstractControl);
      return validator && validator['required'];
    }
    
    return false;
  }
}